import { MonetaryAmount, NotificationIcon, TextTooltip } from '@components'
import { getDocumentsPageUrl } from '@constants'
import { useQueryParams, useTheme } from '@hooks'
import { IBankBalanceWarning, IDocument, useTags } from '@query'
import { useDocument } from '@root/query/hooks/documents'
import {
  documentHasAttachment,
  formatCurrency,
  formatDate,
  selectUserNotificationIcon
} from '@utils'
import { AttachmentExemptIcon } from './DocumentForm/AttachmentExemptIcon'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { FaExclamationTriangle, FaFlag, FaLock, FaPaperclip, FaTag } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { DraftBadge } from './DraftBadge'
import { useBusinessContext } from '@root/context'
import { PiLinkSimpleHorizontalBreakFill } from 'react-icons/pi'
import { RelationReadOnlySummary } from './DocumentForm/RelationReadOnlySummary'
import {
  getDocumentRelationContext,
  documentHasRelationDueMismatch
} from './DocumentForm/relationMapperUtils'

interface DocumentListItemProps {
  document: IDocument
  isSelected: boolean
  isLastOfType: boolean
  isFirstOfType: boolean
  preventNavigation?: boolean
}

const bankBalanceWarningTooltip = (warning: IBankBalanceWarning, t: TFunction) => {
  if (warning.accounting_balance == null || warning.bank_balance == null || warning.diff == null) {
    return null
  }

  return t('document.bankBalanceMismatch', {
    accounting: formatCurrency(warning.accounting_balance),
    bank: formatCurrency(warning.bank_balance),
    diff: formatCurrency(warning.diff)
  })
}

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  document,
  isSelected,
  isFirstOfType,
  isLastOfType,
  preventNavigation = false
}) => {
  const { slug } = useBusinessContext()
  const { t } = useTranslation()
  const documentRef = React.useRef(null)
  const theme = useTheme()
  const [params] = useQueryParams()

  const { data } = useTags({ tagIds: document.tag_ids })

  const tags = document.tag_ids.length > 0 ? data?.results || [] : []
  const relations = document?.relations ?? []

  const StatusIcons = React.memo<{
    isFlagged: boolean
    isLocked: boolean
    hasAttachments: boolean
    attachmentExempt: boolean
    euB2bVatDetailsMissing: boolean
    euSaleToPrivatePerson: boolean
    hasContact: boolean
    bankBalanceWarning: IBankBalanceWarning | null
  }>(
    ({
      isFlagged,
      isLocked,
      hasAttachments,
      attachmentExempt,
      euB2bVatDetailsMissing,
      euSaleToPrivatePerson,
      hasContact,
      bankBalanceWarning
    }) => {
      const commonProps = {
        size: 12,
        color: theme.colors.metalGray
      }

      const icons: React.ReactNode[] = []

      if (hasAttachments || attachmentExempt) {
        icons.push(
          <TextTooltip
            key="attachment"
            tooltip={t(hasAttachments ? 'document.attachmentAdded' : 'document.attachmentExempt')}
          >
            {hasAttachments ? (
              <FaPaperclip {...commonProps} />
            ) : (
              <AttachmentExemptIcon {...commonProps} />
            )}
          </TextTooltip>
        )
      }

      if (isLocked) {
        icons.push(<FaLock key="locked" {...commonProps} />)
      }

      if (isFlagged) {
        icons.push(<FaFlag key="flagged" {...commonProps} color={theme.colors.error} />)
      }

      if (euSaleToPrivatePerson || euB2bVatDetailsMissing) {
        icons.push(
          <TextTooltip
            key="eu-vat"
            tooltip={t(
              euSaleToPrivatePerson
                ? 'document.euPrivatePersonList'
                : hasContact
                ? 'document.euB2bVatMissingVatId'
                : 'document.euB2bVatMissingContact'
            )}
          >
            <FaExclamationTriangle {...commonProps} color={theme.colors.warning} />
          </TextTooltip>
        )
      }

      const bankBalanceTooltip = bankBalanceWarning
        ? bankBalanceWarningTooltip(bankBalanceWarning, t)
        : null
      if (bankBalanceTooltip) {
        icons.push(
          <TextTooltip key="bank-balance" tooltip={bankBalanceTooltip}>
            <FaExclamationTriangle {...commonProps} color={theme.colors.warning} />
          </TextTooltip>
        )
      }

      if (icons.length === 0) {
        return null
      }

      return <StatusIconsContainer>{icons}</StatusIconsContainer>
    }
  )

  const contactName = useMemo(() => {
    if (document.contact_name) return document.contact_name
    if (document.import_data?.contact_hint) return document.import_data.contact_hint
    return ''
  }, [document])

  const documentSrc = useMemo(() => {
    return getDocumentsPageUrl(slug, { ...params, id: document.id })
  }, [slug, document.id, params])

  const wrapItem = (item: React.ReactNode) => {
    if (preventNavigation) {
      return <>{item}</>
    }
    return <StyledLink to={documentSrc}>{item}</StyledLink>
  }

  return wrapItem(
    <StyledListItem
      ref={documentRef}
      id={`list-item-document-${document.id}`}
      className={classNames({
        'first-of-type': isFirstOfType,
        'last-of-type': isLastOfType
      })}
      selected={isSelected}
    >
      <ItemRow className="margin-bottom">
        {<NotificationIcon type={selectUserNotificationIcon(document, true)} />}
        {document.is_draft ? <DraftBadge /> : <ItemNumber>{document.number}</ItemNumber>}
        <Spacer />
        <ItemDate>{formatDate(document.date)}</ItemDate>
        <StatusIcons
          isFlagged={document.is_flagged}
          isLocked={document.is_locked}
          hasAttachments={documentHasAttachment(document.attachment_ids)}
          attachmentExempt={document.attachment_exempt}
          euB2bVatDetailsMissing={document.eu_b2b_vat_details_missing}
          euSaleToPrivatePerson={document.eu_sale_to_private_person}
          hasContact={Boolean(document.contact_id)}
          bankBalanceWarning={document.bank_balance_warning}
        />
        {relations.length > 0 && <Linkage document={document} />}
        {tags.length > 0 && (
          <TagDotsContainer>
            {tags.map(tag => (
              <TagIcon key={tag.id} color={tag.color} />
            ))}
          </TagDotsContainer>
        )}
      </ItemRow>
      <ItemRow>
        <ItemContact>{contactName}</ItemContact>
        <Spacer />
        <ItemBalance>
          <MonetaryAmount value={document.balance} showColor />
        </ItemBalance>
      </ItemRow>
      <ItemRow>
        <ItemDescription>{document.description}</ItemDescription>
      </ItemRow>
    </StyledListItem>
  )
}

const Linkage = ({ document }: { document: IDocument }) => {
  const hasMismatch = documentHasRelationDueMismatch(document)
  const Icon = hasMismatch ? StyledRelationMismatchIcon : StyledRelationIcon

  return (
    <TextTooltip
      maxWidth={600}
      renderTooltip={isOpen => (
        <RelationTooltipContent>
          <RelationLinkageTooltip listDocument={document} enabled={isOpen} />
        </RelationTooltipContent>
      )}
    >
      <Icon />
    </TextTooltip>
  )
}

const RelationLinkageTooltip = ({
  listDocument,
  enabled
}: {
  listDocument: IDocument
  enabled: boolean
}) => {
  const { data: detailDocument, isLoading } = useDocument(
    { documentId: listDocument.id },
    { enabled, staleTime: 1000 * 60 * 5 }
  )

  // Show list payload immediately (relations / mismatch). Footer waits for
  // instance network totals that the list endpoint intentionally omits.
  const document = detailDocument ?? listDocument
  const isFooterLoading = enabled && isLoading && !detailDocument

  return (
    <RelationReadOnlySummary
      relations={document.relations ?? []}
      themeMode="dark"
      isFooterLoading={isFooterLoading}
      {...getDocumentRelationContext(document)}
    />
  )
}

const TagIcon = ({ color }: { color: string }) => {
  return <FaTag size={12} color={color} />
}

const StyledLink = styled(Link).attrs({
  onClick: e => {
    if (e.shiftKey || e.metaKey) {
      e.preventDefault()
    }
  }
})`
  text-decoration: none !important;
`

const StyledListItem = styled.div<{ selected: boolean }>`
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 2px solid #f6f7f9;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${({ theme, selected }) =>
    selected ? theme.colors.defaultHover : theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.md}rem;

  &:hover {
    .description {
      color: ${({ theme }) => theme.colors.primary};
      text-decoration: none !important;
    }
  }

  &.first-of-type {
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }

  &.last-of-type {
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-bottom: 0;
  }
`

const ItemRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
  min-height: 1.2rem;

  &.margin-bottom {
    margin-bottom: 4px;
  }
`

const ItemDate = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const ItemContact = styled.div`
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ItemBalance = styled.div``

const ItemDescription = styled.span.attrs({ className: 'description' })`
  color: ${({ theme }) => theme.colors.neutralBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const ItemNumber = styled.span`
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  border-radius: 0.4rem;
`

const Spacer = styled.div`
  flex: 1;
`

const StatusIconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const TagDotsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const StyledRelationIcon = styled(PiLinkSimpleHorizontalBreakFill)`
  fill: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  box-sizing: content-box;

  &:hover {
    fill: ${({ theme }) => theme.colors.primary};
  }
`

const StyledRelationMismatchIcon = styled(FaExclamationTriangle)`
  fill: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  box-sizing: content-box;

  &:hover {
    fill: ${({ theme }) => theme.colors.warning}88;
  }
`

const RelationTooltipContent = styled.div`
  width: 80vw;
  max-width: 340px;
`
